/**
 * API Configuration — all endpoints in one place
 */
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8787';
const API_VERSION = import.meta.env.VITE_API_VERSION || 'v1';

export const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || '';
export const GOOGLE_REDIRECT_URI = `${window.location.origin}/auth/callback`;

export const API_CONFIG = {
  BASE_URL: API_BASE_URL,
  VERSION: API_VERSION,
  FULL_URL: `${API_BASE_URL}/api/${API_VERSION}`,
  TIMEOUT: 30000,
};

export const API_ENDPOINTS = {
  AUTH: {
    LOGIN: '/auth/login',
    LOGIN_COMPLETE: '/auth/login/complete',
    GOOGLE: '/auth/google',
    REFRESH: '/auth/refresh',
    ME: '/auth/me',
    REGISTER: '/auth/register',
    SETUP_PIN: '/auth/setup-pin',
    VERIFY_PIN: '/auth/verify-pin',
  },
  CLIENTS: {
    LIST: '/clients',
    CREATE: '/clients',
    GET: (id: string) => `/clients/${id}`,
    UPDATE: (id: string) => `/clients/${id}`,
    DELETE: (id: string) => `/clients/${id}`,
    PETS: (id: string) => `/clients/${id}/pets`,
  },
  PATIENTS: {
    LIST: '/patients',
    CREATE: '/patients',
    GET: (id: string) => `/patients/${id}`,
    UPDATE: (id: string) => `/patients/${id}`,
    DELETE: (id: string) => `/patients/${id}`,
    HISTORY: (id: string) => `/patients/${id}/history`,
    SPECIES: '/patients/ref/species',
    CREATE_SPECIES: '/patients/ref/species',
    BREEDS: '/patients/ref/breeds',
    CREATE_BREED: '/patients/ref/breeds',
  },
  APPOINTMENTS: {
    LIST: '/appointments',
    CREATE: '/appointments',
    GET: (id: string) => `/appointments/${id}`,
    UPDATE: (id: string) => `/appointments/${id}`,
    COMPLETE: (id: string) => `/appointments/${id}/complete`,
    CANCEL: (id: string) => `/appointments/${id}/cancel`,
    REF_VETS: '/appointments/ref/vets',
    REF_SERVICES: '/appointments/ref/services',
    REF_TYPES: '/appointments/ref/types',
  },
  MEDICAL_RECORDS: {
    LIST: '/medical-records',
    CREATE: '/medical-records',
    GET: (id: string) => `/medical-records/${id}`,
    UPDATE: (id: string) => `/medical-records/${id}`,
    NOTEBOOK: '/medical-records/notebook',
    NOTEBOOK_BY_APPOINTMENT: (appointmentId: number | string) => `/medical-records/notebook/by-appointment/${appointmentId}`,
    NOTEBOOK_UPDATE: (id: number | string) => `/medical-records/notebook/${id}`,
  },
  PRESCRIPTIONS: {
    LIST: '/prescriptions',
    CREATE: '/prescriptions',
    GET: (id: string) => `/prescriptions/${id}`,
    DISPENSE: (id: string) => `/prescriptions/${id}/dispense`,
  },
  INVENTORY: {
    PRODUCTS: '/inventory/products',
    GET_PRODUCT: (id: string) => `/inventory/products/${id}`,
    CREATE_PRODUCT: '/inventory/products',
    UPDATE_PRODUCT: (id: string) => `/inventory/products/${id}`,
    ADJUST: (id: string) => `/inventory/products/${id}/adjust`,
    MOVEMENTS: (id: string) => `/inventory/products/${id}/movements`,
    LOW_STOCK: '/inventory/alerts/low-stock',
    CATEGORIES: '/inventory/categories',
  },
  INVOICING: {
    INVOICES: '/invoicing/invoices',
    GET_INVOICE: (id: string) => `/invoicing/invoices/${id}`,
    CREATE_INVOICE: '/invoicing/invoices',
    PAYMENT: (id: string) => `/invoicing/invoices/${id}/payment`,
  },
  PAYMENTS: {
    CONFIG: '/payments/config',
    CHECKOUT_SESSION: (id: string | number) => `/payments/invoices/${id}/checkout-session`,
    PAYMENT_INTENT: (id: string | number) => `/payments/invoices/${id}/payment-intent`,
    PAYMENT_STATUS: (id: string | number) => `/payments/invoices/${id}/status`,
    WEBHOOK: '/payments/webhooks/stripe',
  },
  QUEUE: {
    LIST: '/queue',
    CREATE: '/queue',
    ENTRIES: (queueId: string) => `/queue/${queueId}/entries`,
    ALL_ENTRIES: '/queue/entries',
    ADD_ENTRY: (queueId: string) => `/queue/${queueId}/entries`,
    UPDATE_ENTRY: (queueId: string, entryId: string) => `/queue/${queueId}/entries/${entryId}`,
  },
  DASHBOARD: {
    OVERVIEW: '/dashboard',
    REVENUE: '/dashboard/revenue',
  },
  SERVICES: {
    LIST: '/services',
    CREATE: '/services',
    DETAIL: (id: number | string) => `/services/${id}`,
    UPDATE: (id: number | string) => `/services/${id}`,
  },
  REPORTS: {
    APPOINTMENTS: '/reports/appointments',
    REVENUE: '/reports/revenue',
    CLIENTS: '/reports/clients',
    PATIENTS: '/reports/patients',
    OUTSTANDING: '/reports/outstanding',
  },
  ADMIN: {
    USERS: '/admin/users',
    USER: (id: number | string) => `/admin/users/${id}`,
    ROLES: '/admin/roles',
    ROLE_PERMISSIONS: (id: number | string) => `/admin/roles/${id}/permissions`,
    SITE: '/admin/site',
    LOCATIONS: '/admin/locations',
    LOCATION: (id: number | string) => `/admin/locations/${id}`,
    SUPER_SITES: '/admin/super/sites',
    // Master data
    SPECIES: '/admin/species',
    SPECIES_ITEM: (id: number | string) => `/admin/species/${id}`,
    BREEDS: '/admin/breeds',
    BREED_ITEM: (id: number | string) => `/admin/breeds/${id}`,
    APPOINTMENT_TYPES: '/admin/appointment-types',
    APPOINTMENT_TYPE_ITEM: (id: number | string) => `/admin/appointment-types/${id}`,
    SEED_MASTER_DATA: '/admin/seed-master-data',
  },
  LAB_RESULTS: {
    LIST: '/lab-results',
    STATS: '/lab-results/stats',
    CREATE: '/lab-results',
    DETAIL: (id: number | string) => `/lab-results/${id}`,
    UPDATE: (id: number | string) => `/lab-results/${id}`,
    DELETE: (id: number | string) => `/lab-results/${id}`,
  },
  BOARDING: {
    LIST: '/boarding',
    CREATE: '/boarding',
    DETAIL: (id: number | string) => `/boarding/${id}`,
    UPDATE: (id: number | string) => `/boarding/${id}`,
    CHECKIN: (id: number | string) => `/boarding/${id}/checkin`,
    CHECKOUT: (id: number | string) => `/boarding/${id}/checkout`,
    DELETE: (id: number | string) => `/boarding/${id}`,
  },
  REMINDERS: {
    LIST: '/reminders',
    CREATE: '/reminders',
    UPDATE: (id: number | string) => `/reminders/${id}`,
    SEND: (id: number | string) => `/reminders/${id}/send`,
    DELETE: (id: number | string) => `/reminders/${id}`,
  },
  SETTINGS: {
    GET: '/settings',
    UPDATE_SITE: '/settings/site',
    SUBSCRIPTION: '/settings/subscription',
    UPDATE_SUBSCRIPTION: '/settings/subscription',
  },
  UPLOADS: {
    UPLOAD: '/uploads',
    GET: (key: string) => `/uploads/${key}`,
    DELETE: (key: string) => `/uploads/${key}`,
  },
  PURCHASE_ORDERS: {
    LIST: '/purchase-orders',
    CREATE: '/purchase-orders',
    DETAIL: (id: number | string) => `/purchase-orders/${id}`,
    UPDATE: (id: number | string) => `/purchase-orders/${id}`,
    DELETE: (id: number | string) => `/purchase-orders/${id}`,
    RECEIVE: (id: number | string) => `/purchase-orders/${id}/receive`,
  },
  ENUM_VALUES: {
    LIST: '/enum-values',
    GET: (enumName: string) => `/enum-values/${enumName}`,
  },
  VACCINATIONS: {
    LIST: '/vaccinations',
    CREATE: '/vaccinations',
    DUE_SOON: '/vaccinations/due-soon',
    UPDATE: (id: number | string) => `/vaccinations/${id}`,
  },
  CONTROLLED_DRUGS: {
    LIST: '/controlled-drugs',
    CREATE: '/controlled-drugs',
    BALANCE: '/controlled-drugs/balance',
    MEDICATIONS: '/controlled-drugs/medications',
    GET: (id: number | string) => `/controlled-drugs/${id}`,
    CORRECTION: (id: string) => `/controlled-drugs/${id}/correction`,
    VERIFY: (medication: string) => `/controlled-drugs/verify/${encodeURIComponent(medication)}`,
    CATALOG_LIST: '/controlled-drugs/catalog',
    CATALOG_CREATE: '/controlled-drugs/catalog',
    CATALOG_UPDATE: (id: number) => `/controlled-drugs/catalog/${id}`,
    CATALOG_DELETE: (id: number) => `/controlled-drugs/catalog/${id}`,
  },
  SURGERIES: {
    LIST: '/surgeries',
    CREATE: '/surgeries',
    GET: (id: number | string) => `/surgeries/${id}`,
    UPDATE: (id: number | string) => `/surgeries/${id}`,
    COMPLETE: (id: number | string) => `/surgeries/${id}/complete`,
    ANESTHESIA: (id: number | string) => `/surgeries/${id}/anesthesia`,
  },
  RECALL_CAMPAIGNS: {
    LIST: '/recall-campaigns',
    CREATE: '/recall-campaigns',
    GET: (id: number | string) => `/recall-campaigns/${id}`,
    UPDATE: (id: number | string) => `/recall-campaigns/${id}`,
    PREVIEW: (id: number | string) => `/recall-campaigns/${id}/preview`,
    SEND: (id: number | string) => `/recall-campaigns/${id}/send`,
    DELETE: (id: number | string) => `/recall-campaigns/${id}`,
  },
  REPORT_BUILDER: {
    RUN: '/report-builder/run',
    ENTITIES: '/report-builder/entities',
  },
  PAYMENT_PLANS: {
    LIST: '/payment-plans',
    CREATE: '/payment-plans',
    GET: (id: number | string) => `/payment-plans/${id}`,
    UPDATE: (id: number | string) => `/payment-plans/${id}`,
    INSTALMENTS: (id: number | string) => `/payment-plans/${id}/instalments`,
    PAY_INSTALMENT: (planId: number | string, instalmentId: number | string) =>
      `/payment-plans/${planId}/instalments/${instalmentId}/pay`,
  },
  NOTIFICATIONS: {
    LIST: '/notifications',
    UNREAD_COUNT: '/notifications/unread-count',
    CREATE: '/notifications',
    MARK_READ: (id: number | string) => `/notifications/${id}/read`,
    MARK_ALL_READ: '/notifications/read-all',
    DELETE: (id: number | string) => `/notifications/${id}`,
    PREFERENCES: '/notifications/preferences',
  },
  PORTAL: {
    LOGIN: '/portal/auth/login',
    REGISTER: '/portal/auth/register',
    CHANGE_PASSWORD: '/portal/auth/change-password',
    ME: '/portal/me',
    PETS: '/portal/pets',
    APPOINTMENTS: '/portal/appointments',
    BOOK_APPOINTMENT: '/portal/appointments/book',
    CANCEL_APPOINTMENT: (id: number | string) => `/portal/appointments/${id}/cancel`,
    INVOICES: '/portal/invoices',
    VACCINATIONS: '/portal/vaccinations',
    MEDICAL_RECORDS: '/portal/medical-records',
    PRESCRIPTIONS: '/portal/prescriptions',
    REFILL: (id: number | string) => `/portal/prescriptions/${id}/refill`,
    REF_VETS: '/portal/ref/vets',
    REF_SERVICES: '/portal/ref/services',
    REF_APPOINTMENT_TYPES: '/portal/ref/appointment-types',
  },
  ADMIN_SUPER: {
    DASHBOARD: '/admin/super/dashboard',
    SITES: '/admin/super/sites',
  },
  AI: {
    SOAP_ASSIST: '/ai/soap-assist',
    PATIENT_SUMMARY: '/ai/patient-summary',
    NO_SHOW_RISK: (id: number | string) => `/ai/no-show-risk/${id}`,
    INVENTORY_FORECAST: '/ai/inventory-forecast',
  },
  ACCOUNTING: {
    EXPORT: '/accounting/export',
    SUMMARY: '/accounting/summary',
  },
  ROSTER: {
    LIST: '/roster',
    CREATE: '/roster',
    UPDATE: (id: number | string) => `/roster/${id}`,
    DELETE: (id: number | string) => `/roster/${id}`,
    TEMPLATES: '/roster/templates',
  },
  COMPLIANCE: {
    EXPORT: (clientId: number | string) => `/compliance/export/${clientId}`,
    ERASE: (clientId: number | string) => `/compliance/erase/${clientId}`,
    AUDIT_LOG: '/compliance/audit-log',
  },
  FHIR: {
    PATIENT: (id: number | string) => `/fhir/Patient/${id}`,
    EVERYTHING: (id: number | string) => `/fhir/Patient/${id}/$everything`,
    METADATA: '/fhir/metadata',
  },
  EMAIL_QUEUE: {
    LIST: '/email-queue',
    STATS: '/email-queue/stats',
    PROCESS: '/email-queue/process',
    CANCEL: (id: number | string) => `/email-queue/${id}/cancel`,
    RETRY: (id: number | string) => `/email-queue/${id}/retry`,
  },
  ADMIN_BRANDING: {
    GET: '/admin/branding',
    UPDATE: '/admin/branding',
  },
  LOGS: {
    LIST: '/logs',
    STATS: '/logs/stats',
    PURGE: '/logs',
  },
};

export default API_CONFIG;
